import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useGetConnectorHandler } from '../dataHandlers/getConnectorHandler';
import { useGetVersionsHandler } from '../dataHandlers/getVersionsHandler';
import { useCreateVersionHandler } from '../dataHandlers/mutateConnectorHandlers';
import { useRequiredFieldsHandler } from '../dataHandlers/getSchemaHandler';
import { DEFAULT_MARMIN_API_VERSION } from '../constants/connectorsApi';
import { buildInitialRawConfig } from '../utils/dslPayloadBuilder';
import { getApiErrorMessage, getRequiredFields } from '../dataHandlers/connectorsApi';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';

const ConnectorDetailPage = () => {
  const { connectorKey } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const decodedKey = decodeURIComponent(connectorKey);

  const { connector, isLoading: connectorLoading } = useGetConnectorHandler(decodedKey);
  const { versions, isLoading: versionsLoading } = useGetVersionsHandler(decodedKey);
  const marminApiVersion = versions[0]?.marmin_api_version || DEFAULT_MARMIN_API_VERSION;
  const { manifest } = useRequiredFieldsHandler(connector?.document_type, marminApiVersion);

  useDocumentTitle(connector?.name ? `${connector.name} — Connectors` : 'Connector');

  const { mutate: createVersion, isPending: isCreatingVersion } = useCreateVersionHandler({
    onSuccess: (version) => {
      navigate(`/connectors/${encodeURIComponent(decodedKey)}/versions/${version.version}/builder`);
    },
  });

  const handleNewVersion = async () => {
    try {
      const schemaManifest = manifest || (await getRequiredFields(connector.document_type, marminApiVersion));
      const rawConfig = buildInitialRawConfig({
        target: connector.document_type,
        manifest: schemaManifest,
        mappingId: connector.key,
      });

      createVersion({
        connectorKey: decodedKey,
        payload: {
          marminApiVersion,
          rawConfig,
        },
      });
    } catch (error) {
      dispatch(
        showSnackbar({
          message: getApiErrorMessage(error),
          severity: 'error',
        }),
      );
    }
  };

  const handleClone = (sourceVersion) => {
    createVersion({
      connectorKey: decodedKey,
      payload: { cloneFromVersion: sourceVersion.version },
    });
  };

  const isLoading = connectorLoading || versionsLoading;

  return (
    <Box
      sx={{
        height: '86vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        p: 1,
        pb: 4,
        color: '#fff',
        ml: { xs: 2, md: 10 },
        mr: { xs: 2, md: 10 },
      }}
    >
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component="button" underline="hover" color="inherit" onClick={() => navigate('/connectors')}>
          Connectors
        </Link>
        <Typography color="text.primary">{decodedKey}</Typography>
      </Breadcrumbs>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/connectors')} sx={{ mb: 1 }}>
            Back
          </Button>
          <Typography variant="h5" fontWeight={600}>
            {connector?.name || decodedKey}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {connector?.document_type}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={!connector || isCreatingVersion}
            onClick={handleNewVersion}
          >
            New version
          </Button>
        </Stack>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Card sx={{ backgroundColor: 'background.paper' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>API version</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary" sx={{ py: 4 }}>
                        No versions yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {[...versions]
                  .sort((a, b) => b.version - a.version)
                  .map((v) => (
                    <TableRow key={v.id || v.version}>
                      <TableCell>v{v.version}</TableCell>
                      <TableCell>
                        <Chip
                          label={v.status}
                          size="small"
                          color={v.status === 'ACTIVE' ? 'success' : v.status === 'DRAFT' ? 'default' : 'warning'}
                        />
                      </TableCell>
                      <TableCell>{v.marmin_api_version}</TableCell>
                      <TableCell>{v.updated_at ? new Date(v.updated_at * 1000).toLocaleString() : '—'}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            onClick={() =>
                              navigate(`/connectors/${encodeURIComponent(decodedKey)}/versions/${v.version}`)
                            }
                          >
                            View
                          </Button>
                          {(v.status === 'DRAFT' || v.status === 'TESTING') && (
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() =>
                                navigate(`/connectors/${encodeURIComponent(decodedKey)}/versions/${v.version}/builder`)
                              }
                            >
                              Edit
                            </Button>
                          )}
                          <Button size="small" onClick={() => handleClone(v)} disabled={isCreatingVersion}>
                            Clone
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
    </Box>
  );
};

export default ConnectorDetailPage;
